import type {
  ActivationConfig,
  Announcement,
  MessageThread,
  WherdUser,
  WherdView,
  ThemeTokens,
  WidgetConfig,
} from "./types";

export interface WherdState {
  /** True once init() (config load + identity) has completed. */
  ready: boolean;
  isOpen: boolean;
  view: WherdView;
  config: WidgetConfig | null;
  /** Theme passed by the developer via the Provider — overrides server config. */
  localTheme: ThemeTokens | null;
  /** Activation overrides from the Provider — override server config (fab/shake). */
  localActivation: Partial<ActivationConfig> | null;
  user: WherdUser | null;
  verified: boolean;
  /** Unread changelog count — drives the "What's New" badge app-wide. */
  changelogUnread: number;
  /** The user's message threads (shared so live updates reach every view). */
  messages: MessageThread[];
  /** Unread admin replies across the user's message threads. */
  messagesUnread: number;
  /** Active, non-dismissed announcements (banners). */
  announcements: Announcement[];
  /** Bumped on each live feature-request event so list hooks refetch. */
  featureRequestsRevision: number;
}

// Minimal observable store, compatible with React's useSyncExternalStore.
export class Store {
  private state: WherdState;
  private listeners = new Set<() => void>();

  constructor(initial: WherdState) {
    this.state = initial;
  }

  getSnapshot = (): WherdState => this.state;

  subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  };

  set(patch: Partial<WherdState>): void {
    this.state = { ...this.state, ...patch };
    for (const l of this.listeners) l();
  }
}
